import React from 'react';
import ExecutiveSummary from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/ExecutiveSummary';
import { KnownExtraSections } from '@lifeomic/phc-web-toolkit/src/components/AITemplates/ImpressionTypeFilter';
import { KnownImpressionTypes } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';
import { ModuleType } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/basicModuleTypesConfig';
import {
  DashboardConfig,
  ModuleConfig,
} from '@lifeomic/phc-web-toolkit/src/components/LayoutConfiguration/types';
import { useHistory, useParams } from 'react-router-dom';
import urls from '../../../../util/urls';
import MemberDetail from './index';

const annualReportModuleConfig: ModuleConfig<{ displayAsDuration: boolean }> = {
  version: 1,
  module: {
    key: 'member-annual-report',
    moduleType: ModuleType.AnnualReport,
    title: 'Annual Report',
    layout: {
      w: 12,
      h: 20,
      minW: 1,
      minH: 1,
      x: 0,
      y: 0,
      isResizable: true,
    },
  },
  component: { displayAsDuration: false },
};

const annualReportDashboardConfig: DashboardConfig = {
  version: 1,
  modules: [annualReportModuleConfig],
};

const annualReportInjectedPropsBase = {
  moduleKey: annualReportModuleConfig.module.key,
  isModuleVisible: true,
  dateTickFilter: { min: 0, max: 0 },
  dateTickBounds: { min: 0, max: 0 },
  dateFilter: { min: new Date(0), max: new Date() },
  isInEditMode: false,
  deviceId: 'member-detail',
  moduleConfig: annualReportModuleConfig,
  onModuleConfigChange: () => undefined,
  onDynamicDataResolved: () => ({}),
  selectedDashboardConfig: annualReportDashboardConfig,
} as const;

type AnnualReportContentProps = {
  member: fhir.Patient | null;
  memberId: string;
  fallback: React.ReactNode;
};

export const AnnualReportContent: React.FC<AnnualReportContentProps> = ({
  member,
  memberId,
  fallback,
}) => {
  const history = useHistory();
  const { annualReportId, annualReportSection } = useParams<{
    memberId: string;
    annualReportId?: string;
    annualReportSection?: string;
  }>();

  if (!member) {
    return <>{fallback}</>;
  }

  const patientId = member.id || memberId;

  const getAnnualReportPath = (reportId?: string, section?: string) => {
    const basePath = `${urls.app.members(memberId)}/annual-report`;
    if (reportId && section) {
      return `${basePath}/${reportId}/${section}`;
    }
    return basePath;
  };

  const selectedSection =
    (annualReportSection as KnownImpressionTypes | KnownExtraSections) ||
    undefined;

  return (
    <ExecutiveSummary
      {...annualReportInjectedPropsBase}
      selectedReportId={annualReportId || undefined}
      selectedSection={selectedSection}
      onReportChange={reportId => {
        const nextPath = getAnnualReportPath(reportId, 'overview');
        if (history.location.pathname !== nextPath) {
          history.push(nextPath);
        }
      }}
      onSectionChange={section => {
        const activeReportId =
          annualReportId ||
          window.localStorage.getItem(`${patientId}-carePlanId`) ||
          undefined;
        if (!activeReportId) {
          return;
        }
        const nextPath = getAnnualReportPath(activeReportId, section);
        if (history.location.pathname !== nextPath) {
          history.push(nextPath);
        }
      }}
      patient={{
        ...member,
        id: patientId,
      }}
    />
  );
};

const MemberAnnualReport: React.FC = () => {
  return <MemberDetail section="annual-report" />;
};

MemberAnnualReport.displayName = 'MemberAnnualReport';
export default MemberAnnualReport;
